import { Role } from '@gm-commercial/profile-model';
import type { ProfileResponse } from '@gm-commercial/rtk-clients/users/types';
import { ADMIN_ROLES } from '~/common/constants/common.constant';
import {
  type ActivationRateEvent,
  type ActivationRateProperties,
  type CspHeapContext,
  HEAP_ENTRY_POINTS,
  HEAP_EVENTS,
  HEAP_PLATFORMS,
  type HeapEntryPoint,
  type HeapNavigationState,
  type HeapOrganizationDetails,
  type HeapPlatform,
  type VehicleManuallyAddedProperties,
} from '~/common/models/heap.model';

/**
 * Whether this page is inside the native mobile app.
 * The app injects a bridge on `window`; there is no standard WebView API.
 */
const isNativeMobileApp = (): boolean =>
  Boolean(window.AndroidBridge?.onAccessTokenReceived) ||
  Boolean(window.webkit?.messageHandlers?.iosBridge);

/**
 * Whether the page was launched as an installed PWA.
 * Chromium and current iOS report `display-mode: standalone`, matching the
 * manifest. Older iOS home-screen launches only set `navigator.standalone`.
 */
const isProgressiveWebApp = (): boolean => {
  const launchedStandalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches === true;
  const navigatorWithStandalone = window.navigator as Navigator & {
    standalone?: boolean;
  };

  return launchedStandalone || navigatorWithStandalone.standalone === true;
};

/**
 * Resolves the Heap `platform` from the current window.
 * The native bridge is checked first so a chromeless WebView is `Mobile`
 * even when it also matches a standalone display mode.
 * @returns `Mobile` inside the native app, `PWA` when installed, otherwise `Web`
 */
export const getHeapPlatform = (): HeapPlatform => {
  if (typeof window === 'undefined') {
    return HEAP_PLATFORMS.WEB;
  }
  if (isNativeMobileApp()) {
    return HEAP_PLATFORMS.MOBILE;
  }
  if (isProgressiveWebApp()) {
    return HEAP_PLATFORMS.PWA;
  }
  return HEAP_PLATFORMS.WEB;
};

const CSP_APP_ENTRY_SESSION_KEY = 'heap_csp_app_entry_sent';

/**
 * Maps an API role code (`SYSTEM_MANAGER`) to the same display `Role` the store
 * profile uses (`Advisor`), so Heap `role` stays consistent with develop.
 * A value that is already a mapped `Role` is returned as-is.
 * @param role - Role from the signed-in profile
 * @returns Mapped `Role`, or undefined when no role is present
 */
const toMappedRole = (role?: string): Role | undefined => {
  if (!role) {
    return undefined;
  }
  return Role[role as keyof typeof Role] ?? (role as Role);
};

/**
 * Maps an API role code (`SYSTEM_MANAGER`) or a mapped `Role` value (`Advisor`)
 * onto the advisor analytics audience, including restricted GMF advisors.
 * @param role - Role from the signed-in profile
 * @returns Whether the role is an admin/advisor role
 */
const isAdvisorRole = (role?: string): boolean => {
  const mappedRole = toMappedRole(role);
  return Boolean(
    mappedRole &&
      (mappedRole === Role.GMF_ADVISOR || ADMIN_ROLES.includes(mappedRole))
  );
};

/**
 * Builds Heap user properties from the signed-in profile and organization details.
 * @param profile - Signed-in user as returned by `/me`
 * @param organization - Current organization details, when they have loaded
 * @param locale - Locale the app is rendering in, when one is known
 * @returns Properties to send to Heap `addUserProperties`
 */
export const buildHeapUserProperties = (
  profile: ProfileResponse,
  organization?: HeapOrganizationDetails,
  locale?: string
): Record<string, unknown> => {
  const emailDomain = profile.email?.match(/(?<=@).*/);
  const organizationName = organization?.name ?? profile.organization?.name;
  // The organizations API omits orgType when the classification is CUSTOMER.
  const orgType = organization
    ? (organization.orgType ?? 'CUSTOMER')
    : 'Unknown';

  return {
    emailDomain: emailDomain?.[0] ?? 'Unknown',
    cdiId: profile.cdiProfileId ?? null,
    orgId: profile.organization?.id ?? organization?.id ?? 'Unknown',
    orgName: organizationName ?? 'Unknown',
    orgVerificationStatus: organization?.verificationStatus ?? 'Unknown',
    orgType,
    fan: organization?.fan ?? 'Unknown',
    role: toMappedRole(profile.role) ?? 'Unknown',
    isAdvisor: isAdvisorRole(profile.role),
    isMigrated: profile?.migrated ?? 'Unknown',

    isTestUser:
      Boolean(organizationName?.toLowerCase().includes('test')) ||
      orgType === 'TEST' ||
      orgType === 'DEMO',
    platform: getHeapPlatform(),
    locale: locale ?? 'Unknown',
  };
};

/**
 * Updates the Heap `locale` user property for the signed-in user.
 * Skipped unless Heap is identified as that user, so the value never lands on
 * an anonymous or a previous user's identity.
 * @param profileId - Id of the signed-in profile
 * @param locale - Locale the app is rendering in, when one is known
 * @returns Whether Heap `addUserProperties` was called
 */
export const syncHeapUserLocale = (
  profileId: string | undefined,
  locale: string | undefined
): boolean => {
  if (
    !profileId ||
    typeof window === 'undefined' ||
    typeof window.heap?.getIdentity !== 'function' ||
    typeof window.heap.addUserProperties !== 'function' ||
    window.heap.getIdentity() !== profileId
  ) {
    return false;
  }

  window.heap.addUserProperties({ locale: locale ?? 'Unknown' });
  return true;
};

/**
 * Sends an event to Heap.
 * A missing SDK or a throw from `track` is reported as not sent, so analytics
 * cannot fail the workflow that called it.
 * @param eventName - Heap event name
 * @param properties - Event properties
 * @returns Whether Heap `track` completed
 */
export const dispatchHeapEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.heap?.track !== 'function'
  ) {
    return false;
  }

  try {
    window.heap.track(eventName, properties);
  } catch {
    return false;
  }

  return true;
};

/**
 * Tracks an activation-rate add event after a successful add API call.
 * Omits `AmountFailedToAdd` when the API does not report failures.
 * @param eventName - Vehicle, user, or order added event
 * @param properties - Counts and organization id for the add attempt
 * @returns Whether Heap `track` was called
 */
export const trackActivationRateAdded = (
  eventName: ActivationRateEvent,
  { AmountAdded, AmountFailedToAdd, OrgId }: ActivationRateProperties
): boolean => {
  const properties: Record<string, unknown> = { AmountAdded, OrgId };
  if (AmountFailedToAdd !== undefined) {
    properties.AmountFailedToAdd = AmountFailedToAdd;
  }
  return dispatchHeapEvent(eventName, properties);
};

/**
 * Tracks a vehicle add attempt when the user submits the add-vehicles modal.
 * @param properties - Attempt count, organization id, and add path
 * @returns Whether Heap `track` was called
 */
export const trackVehicleManuallyAdded = (
  properties: VehicleManuallyAddedProperties
): boolean =>
  dispatchHeapEvent(
    HEAP_EVENTS.ACTIVATION_RATE_VEHICLE_MANUALLY_ADDED,
    properties
  );

/**
 * Tracks a vehicle add attempt when the user confirms auto-onboarding import.
 * @param properties - Attempt count and organization id
 * @returns Whether Heap `track` was called
 */
export const trackVehicleAutomaticallyAdded = ({
  AmountAdded,
  OrgId,
}: Pick<ActivationRateProperties, 'AmountAdded' | 'OrgId'>): boolean =>
  dispatchHeapEvent(HEAP_EVENTS.ACTIVATION_RATE_VEHICLE_AUTOMATICALLY_ADDED, {
    AmountAdded,
    OrgId,
  });

/**
 * Attaches properties to every subsequent Heap event in this session.
 * @param properties - Properties Heap merges into each tracked event
 * @returns Whether Heap `addEventProperties` was called
 */
export const setHeapEventProperties = (
  properties: Record<string, unknown>
): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.heap?.addEventProperties !== 'function'
  ) {
    return false;
  }

  window.heap.addEventProperties(properties);
  return true;
};

/**
 * Removes every property attached with `setHeapEventProperties`, so events
 * sent without a selected context do not carry the previous one.
 * @returns Whether Heap `clearEventProperties` was called
 */
export const clearHeapEventProperties = (): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.heap?.clearEventProperties !== 'function'
  ) {
    return false;
  }

  window.heap.clearEventProperties();
  return true;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads the Heap attribution fields from router state, ignoring anything else stored there.
 * @param state - `location.state` from React Router
 * @returns The attribution fields that are present
 */
export const readHeapNavigationState = (
  state: unknown
): HeapNavigationState => {
  if (!isRecord(state)) return {};

  const { entryPoint, listSortColumn, driverScoreId, activeFilterState } =
    state;
  const knownEntryPoints: readonly unknown[] = Object.values(HEAP_ENTRY_POINTS);

  return {
    ...(knownEntryPoints.includes(entryPoint) && {
      entryPoint: entryPoint as HeapEntryPoint,
    }),
    ...(typeof listSortColumn === 'string' && { listSortColumn }),
    ...(typeof driverScoreId === 'string' && { driverScoreId }),
    ...(isRecord(activeFilterState) && { activeFilterState }),
  };
};

/**
 * Resolves how the user reached the current page.
 * @param state - `location.state` from React Router
 * @returns The entry point the navigation recorded, or `DIRECT_LINK` when none was recorded
 */
export const getHeapEntryPoint = (state: unknown): HeapEntryPoint =>
  readHeapNavigationState(state).entryPoint ?? HEAP_ENTRY_POINTS.DIRECT_LINK;

/**
 * Tracks the first authenticated CSP shell entry once per browser session.
 */
export const trackCspAppEntryOncePerSession = (
  context: CspHeapContext
): void => {
  if (typeof window === 'undefined') return;

  if (sessionStorage.getItem(CSP_APP_ENTRY_SESSION_KEY)) return;

  const wasDispatched = dispatchHeapEvent(HEAP_EVENTS.CSP_APP_ENTRY, {
    organizationId: context.organizationId ?? 'unknown',
    fleetId: context.fleetId ?? 'unknown',
    path: context.path ?? window.location.pathname,
    isEmbedded: true,
  });

  if (wasDispatched) {
    sessionStorage.setItem(CSP_APP_ENTRY_SESSION_KEY, 'true');
  }
};

/**
 * Clears the Heap identity on logout so the next session is not attributed
 * to the previous user.
 * @returns Whether Heap `resetIdentity` was called
 */
export const resetHeapIdentity = (): boolean => {
  if (
    typeof window === 'undefined' ||
    typeof window.heap?.resetIdentity !== 'function'
  ) {
    return false;
  }

  window.heap.resetIdentity();
  return true;
};
